<template>
  <div :class="[is_pinned ? 'relative h-full' : 'flex h-full']">
    <!-- Left resize handle with two stripes -->
    <div
      v-if="!mq.isMobile"
      class="w-[0.10rem] h-full hover:bg-primary-500 cursor-col-resize z-50 transition-colors duration-200"
      :class="{ 'bg-red-300': isResizing, 'absolute left-0 top-0': is_pinned }"
      @mousedown="startResize"
    />

    <div
      id="sidebar-notifications"
      class="bg-white h-full shadow-left z-40 shadow-lg flex"
      :class="[is_pinned ? '' : 'absolute top-0 right-0 z-50']"
      :style="{ width: mq.isMobile ? '100%' : sidebar_width + 'px' }"
    >
      <div
        v-if="!mq.isMobile"
        class="w-[0.10rem] h-full hover:bg-primary-500 cursor-col-resize z-50 transition-colors duration-200"
        :class="{ 'bg-red-300': isResizing, 'absolute left-0 top-0': is_pinned }"
        @mousedown="startResize"
      />
      <div class="flex flex-col w-full">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2">
            <div
              v-if="!mq.isMobile"
              class="cursor-pointer size-8 flex items-center justify-center rounded-full hover:text-gray-800"
              :class="[is_pinned ? 'bg-gray-200 text-primary-500 ' : 'hover:bg-gray-200 text-gray-400 ']"
              @click="UPDATE_SIDEBAR_PREFS({ is_pinned: !is_pinned })"
            >
              <FontAwesomeIcon :icon="['far', 'thumbtack']" />
            </div>
            <span class="font-semibold text-xl">
              {{ $t('notification.title') }}
            </span>
          </div>
          <div
            class="text-gray-500 hover:bg-gray-200 cursor-pointer size-8 flex items-center justify-center rounded-full hover:text-gray-800"
            data-test="sidebar-notifications-close"
            @click="SHOW_SIDEBAR(null)"
          >
            <FontAwesomeIcon
              :icon="['far', 'xmark']"
              size="lg"
            />
          </div>
        </div>

        <b-loading
          v-if="fetch_notifications_at"
          :is-full-page="false"
          :model-value="true"
        />

        <NotificationsFilter
          class="shrink-0"
          :sort_order="sort_order"
          :sort_order_type="sort_order_type"
          @fetchNotificationSubgroups="fetchNotificationSubgroups"
          @openNotificationSubgroupsAll="openNotificationSubgroupsAll"
          @setSortOrder="
            (new_sort_prefs) => {
              sort_prefs = new_sort_prefs;
            }
          "
        />

        <section
          class="shrink-1 flex-1 grow overflow-y-auto"
          data-test="notification-container"
        >
          <div
            v-if="notification_subgroups_page.length === 0 && !fetch_notifications_at"
            class="has-text-centered mt-5"
          >
            <p class="subtitle has-text-grey">
              <FontAwesomeIcon
                v-if="!is_filtering"
                class="mr-2"
                :icon="['far', 'thumbs-up']"
              />
              {{ $t(is_filtering ? 'notification.labels.noResults' : 'notification.labels.noNewNotification') }}
            </p>
          </div>
          <div
            v-for="(notification_subgroup, index) in notification_subgroups_page"
            :key="notification_subgroup.id"
            :class="{ 'border-b border-gray-100': index < notification_subgroups_page.length - 1 }"
          >
            <NotificationItem
              :notification_subgroup="notification_subgroup"
              @onChangeReadStatus="fetchNotificationSubgroups"
              @onDelete="deleteNotificationSubgroup(notification_subgroup)"
            />
          </div>
        </section>

        <div
          v-if="page_count > 1"
          class="p-2 shrink-0"
          @click.stop
        >
          <b-pagination
            v-model="sidebar_prefs.page_number"
            :order="pagination.order"
            :per-page="page_size"
            :rounded="false"
            :simple="false"
            size="is-small"
            :total="notification_subgroup_pages_length"
            @change="(page_number) => UPDATE_SIDEBAR_PREFS({ page_number })"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { NOTIFICATION_SUBGROUPS_PAGE_SIZE } from '@/app/data/pagination_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import NotificationsFilter from '@/app/shared_components/navbar/notifications/NotificationsFilter.vue';
import NotificationItem from '@/app/shared_components/activities/NotificationItem.vue';
import { differenceInSeconds } from 'date-fns';
import { useMqService } from '@/plugins/mq';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';

const SORT_ORDER_DEFAULT = 'desc'; // Newest first
const SORT_ORDER_TYPE_DEFAULT = 'updated_at';

export default {
  name: 'NotificationsSidebar',
  components: { NotificationsFilter, NotificationItem },

  setup() {
    const mq = useMqService();
    return { mq };
  },

  data() {
    return {
      fetch_notifications_at: false,
      page_size: NOTIFICATION_SUBGROUPS_PAGE_SIZE,
      pagination: {
        order: 'is-centered',
      },
      PROFILE_TYPES,
      SIDEBAR_TYPE_NOTIFICATIONS,
      sort_prefs: [`${SORT_ORDER_TYPE_DEFAULT} ${SORT_ORDER_DEFAULT}`],
      isResizing: false,
      minWidth: 300,
      maxWidth: 800,
    };
  },

  computed: {
    ...mapGetters([
      'current_client',
      'current_profile',
      'customers',
      'employees',
      'notification_subgroup_pages_length',
      'notification_subgroups',
      'notification_subgroups_page',
      'partners',
      'project',
      'projects',
      'sidebar_type',
      'sidebar_prefs',
      'units',
    ]),

    is_filtering() {
      return this.sidebar_prefs.project_id || this.sidebar_prefs.unopen;
    },

    sidebar_width() {
      return this.sidebar_prefs.sidebar_width;
    },

    is_pinned() {
      if (this.mq.isMobile) {
        return false;
      }
      return this.sidebar_prefs.is_pinned;
    },

    page_count() {
      return Math.ceil(this.notification_subgroup_pages_length / this.page_size);
    },

    is_visible() {
      return this.sidebar_type === SIDEBAR_TYPE_NOTIFICATIONS;
    },

    sort_order() {
      return _.last(this.sort_prefs).split(' ')[1];
    },

    sort_order_type() {
      return _.last(this.sort_prefs).split(' ')[0];
    },
  },

  watch: {
    $route: {
      handler() {
        // auto-close sidebar on route change only when it overlaps with content
        if (this.is_visible && !this.mq.isDesktop && !this.is_pinned) {
          this.SHOW_SIDEBAR(null);
        }
      },
      immediate: true,
    },
    is_visible: {
      handler(val) {
        if (val) {
          this.UPDATE_NOTIFICATIONS_LAST_OPENED();
          this.fetchNotificationSubgroups();
        }
      },
    },
    'sidebar_prefs.page_number': {
      handler() {
        this.fetchNotificationSubgroups();
      },
    },
    sort_prefs: {
      handler() {
        this.UPDATE_SIDEBAR_PREFS({ sort_prefs: this.sort_prefs });
        this.fetchNotificationSubgroups();
      },
    },
  },

  created() {
    if (this.sidebar_prefs.sort_prefs) {
      this.sort_prefs = this.sidebar_prefs.sort_prefs;
    } else {
      this.UPDATE_SIDEBAR_PREFS({ sort_prefs: this.sort_prefs });
    }
    this.emitter.$on('fetchNotificationSubgroups', this.fetchNotificationSubgroups);
    this.fetchNotificationSubgroupsDebounce = _.debounce(this.fetchNotificationSubgroups, 1000);
  },

  mounted() {
    document.addEventListener('mousemove', this.handleResize);
    document.addEventListener('mouseup', this.stopResize);
  },

  beforeUnmount() {
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
  },

  methods: {
    ...mapActions([
      'DELETE_NOTIFICATION_SUBGROUP',
      'FETCH_NOTIFICATION_SUBGROUPS_PAGE',
      'OPEN_NOTIFICATION_SUBGROUPS',
      'SHOW_SIDEBAR',
      'UPDATE_NOTIFICATIONS_LAST_OPENED',
      'UPDATE_SIDEBAR_PREFS',
    ]),

    startResize(event) {
      this.isResizing = true;
      this.startX = event.clientX;
      this.startWidth = this.sidebar_width;
      event.preventDefault();
    },

    handleResize(event) {
      if (!this.isResizing) return;

      const deltaX = event.clientX - this.startX;
      const newWidth = this.startWidth - deltaX;

      const sidebar_width = Math.max(this.minWidth, Math.min(this.maxWidth, newWidth));
      this.UPDATE_SIDEBAR_PREFS({ sidebar_width });
    },

    stopResize() {
      if (this.isResizing) {
        this.isResizing = false;
        this.UPDATE_SIDEBAR_PREFS({ sidebar_width: this.sidebar_width });
      }
    },

    async fetchNotificationSubgroups() {
      if (!this.is_visible) return;
      // locking mechanism to avoid concurrent fetches, with fail-safe auto-release after 60s
      if (differenceInSeconds(this.fetch_notifications_at, new Date()) > 60) this.fetch_notifications_at = null;
      if (this.initialized && this.fetch_notifications_at) return;
      this.fetch_notifications_at = new Date();
      // dynamically determine page size based on current window height
      this.page_size = _.max([Math.floor((window.innerHeight - 235) / 75), 10]);
      await this.FETCH_NOTIFICATION_SUBGROUPS_PAGE({ page_size: this.page_size });
      this.fetch_notifications_at = null;
    },

    async openNotificationSubgroupsAll() {
      if (_.isEmpty(this.notification_subgroups_page)) return;
      const ids = _.map(this.notification_subgroups_page, 'id');
      await this.OPEN_NOTIFICATION_SUBGROUPS({ ids });
      await this.fetchNotificationSubgroups();
    },

    deleteNotificationSubgroup(notification_subgroup) {
      this.DELETE_NOTIFICATION_SUBGROUP({ notification_subgroup });
      this.fetchNotificationSubgroupsDebounce();
    },
  },
};
</script>
